// WebSocket service for conversational AI
import io, { Socket } from 'socket.io-client';

export interface ConversationMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  parameters_extracted?: Record<string, any>;
  confidence_score?: number;
  mode?: string;  // Add mode field for AI responses (help, configure, navigate, research)
}

export interface ConversationState {
  parameters: Record<string, any>;
  missing: string[];
  suggestions?: string[];
  confidence?: number;
}

export interface ConfigPreview {
  config: string;
  validation?: {
    is_valid: boolean;
    errors?: string[];
    warnings?: string[];
  };
}

export interface ConversationInfo {
  id: string;
  title: string;
  switch_type: string;
  vendor: string;
  status: 'active' | 'completed' | 'abandoned';
}

class ConversationService {
  private socket: Socket | null = null;
  private conversationId: string | null = null;
  private messageHandlers: Set<(message: ConversationMessage) => void> = new Set();
  private stateHandlers: Set<(state: ConversationState) => void> = new Set();
  private previewHandlers: Set<(preview: ConfigPreview | null) => void> = new Set();
  private connectionHandlers: Set<(connected: boolean) => void> = new Set();

  constructor() {
    // Initialize service
  }

  async connect(conversationId?: string, token?: string): Promise<void> {
    // Token should be passed from the component that got it from Clerk
    const authToken = token;
    
    if (!authToken) {
      throw new Error('Authentication token not provided. Please pass the token from Clerk.');
    }

    // Generate conversation ID if not provided
    this.conversationId = conversationId || this.generateConversationId();
    
    // Build WebSocket URL using configured endpoint
    // Import the config to get the proper WebSocket URL
    const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
    
    // Remove trailing slash if present and add the WebSocket path
    const baseUrl = wsUrl.replace(/\/$/, '');
    const wsEndpoint = `${baseUrl}/ws/conversation/${this.conversationId}?token=${authToken}`;
    
    console.log('Building WebSocket URL:', wsEndpoint);
    
    // Using native WebSocket instead of socket.io for compatibility with FastAPI
    this.connectWebSocket(wsEndpoint);
  }

  private connectWebSocket(url: string, retryCount: number = 0) {
    console.log(`Attempting WebSocket connection to: ${url} (attempt ${retryCount + 1})`);
    
    // Prevent multiple connections in React StrictMode
    if ((this as any).ws && (this as any).ws.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket connection already in progress, skipping duplicate');
      return;
    }
    
    // Close any existing connection
    if ((this as any).ws && 
        ((this as any).ws.readyState === WebSocket.OPEN || 
         (this as any).ws.readyState === WebSocket.CONNECTING)) {
      console.log('Closing existing WebSocket connection');
      (this as any).ws.close();
    }
    
    const ws = new WebSocket(url);
    
    // Set a connection timeout - increase to 10 seconds for proxy connections
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.error('WebSocket connection timeout');
        ws.close();
        
        // Retry logic with exponential backoff
        if (retryCount < 3) {
          const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          console.log(`Retrying connection in ${retryDelay}ms...`);
          setTimeout(() => {
            this.connectWebSocket(url, retryCount + 1);
          }, retryDelay);
        } else {
          console.error('Max retry attempts reached');
          this.notifyConnectionHandlers(false);
        }
      }
    }, 10000); // 10 second timeout
    
    ws.onopen = () => {
      console.log('WebSocket connected successfully');
      clearTimeout(connectionTimeout);
      this.notifyConnectionHandlers(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('WebSocket message received:', data.type);
        this.handleMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      console.error('WebSocket state:', ws.readyState);
      console.error('WebSocket URL:', ws.url);
      clearTimeout(connectionTimeout);
      // Don't notify failure here, let timeout/close handle it
    };

    ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      clearTimeout(connectionTimeout);
      this.notifyConnectionHandlers(false);
    };

    // Store WebSocket reference for sending messages
    (this as any).ws = ws;
  }

  private handleMessage(data: any) {
    switch (data.type) {
      case 'initial_state':
        this.handleInitialState(data);
        break;
      case 'message':
        this.handleChatMessage(data);
        break;
      case 'parameter_update':
        this.handleParameterUpdate(data);
        break;
      case 'config_generated':
        this.handleConfigGenerated(data);
        break;
      case 'navigation_execute':
        this.handleNavigationExecute(data);
        break;
      case 'error':
        console.error('Server error:', data.message);
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  private handleInitialState(data: any) {
    // Handle conversation info
    if (data.conversation) {
      // Could emit conversation info if needed
    }

    // Handle existing messages
    if (data.messages && data.messages.length > 0) {
      data.messages.forEach((msg: any) => {
        this.notifyMessageHandlers({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        });
      });
    }

    // Handle current state
    if (data.state) {
      this.notifyStateHandlers(data.state);
      if (data.state.preview) {
        this.notifyPreviewHandlers({ config: data.state.preview });
      }
    }
  }

  private handleChatMessage(data: any) {
    // Handle new message
    if (data.message) {
      this.notifyMessageHandlers(data.message);
    }

    // Handle state update
    if (data.state) {
      console.log('State update received:', data.state);
      this.notifyStateHandlers(data.state);
    }

    // Handle preview update
    if (data.preview) {
      this.notifyPreviewHandlers(data.preview);
    }
  }

  private handleParameterUpdate(data: any) {
    // Handle preview update after parameter change
    if (data.preview) {
      this.notifyPreviewHandlers(data.preview);
    }
  }

  private handleConfigGenerated(data: any) {
    console.log('Configuration generated:', data.configuration_id);
    // Could navigate to configuration page or show success message
  }

  private handleNavigationExecute(data: any) {
    console.log('Executing navigation:', data.action);
    if (data.action && data.action.target) {
      // Navigate to the target page
      // Using window.location.href for full page navigation
      window.location.href = data.action.target;
    }
  }

  sendMessage(content: string) {
    if (!(this as any).ws || (this as any).ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message = {
      type: 'message',
      content: content
    };

    (this as any).ws.send(JSON.stringify(message));
  }

  sendRawMessage(message: any) {
    if (!(this as any).ws || (this as any).ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    (this as any).ws.send(JSON.stringify(message));
  }

  updateParameter(parameter: string, value: any) {
    if (!(this as any).ws || (this as any).ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    console.log('Updating parameter:', parameter, '=', value);
    const message = {
      type: 'parameter_update',
      parameter: parameter,
      value: value
    };

    (this as any).ws.send(JSON.stringify(message));
  }

  generateConfiguration() {
    if (!(this as any).ws || (this as any).ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message = {
      type: 'generate_config'
    };

    (this as any).ws.send(JSON.stringify(message));
  }

  completeConversation() {
    if (!(this as any).ws || (this as any).ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message = {
      type: 'complete'
    };

    (this as any).ws.send(JSON.stringify(message));
  }

  disconnect() {
    if ((this as any).ws) {
      (this as any).ws.close();
      (this as any).ws = null;
    }
    this.conversationId = null;
  }

  // Event handlers registration
  onMessage(handler: (message: ConversationMessage) => void) {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onStateUpdate(handler: (state: ConversationState) => void) {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  onPreviewUpdate(handler: (preview: ConfigPreview | null) => void) {
    this.previewHandlers.add(handler);
    return () => this.previewHandlers.delete(handler);
  }

  onConnectionChange(handler: (connected: boolean) => void) {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  // Notify handlers
  private notifyMessageHandlers(message: ConversationMessage) {
    this.messageHandlers.forEach(handler => handler(message));
  }

  private notifyStateHandlers(state: ConversationState) {
    this.stateHandlers.forEach(handler => handler(state));
  }

  private notifyPreviewHandlers(preview: ConfigPreview | null) {
    this.previewHandlers.forEach(handler => handler(preview));
  }

  private notifyConnectionHandlers(connected: boolean) {
    this.connectionHandlers.forEach(handler => handler(connected));
  }

  private generateConversationId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // REST API methods for conversation management
  async listConversations(token?: string): Promise<any> {
    // Token must be passed from component that gets it from Clerk
    if (!token) {
      console.warn('No authentication token provided for listConversations');
      return { conversations: [], total: 0 };
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('Conversations endpoint not found - returning empty list');
          return { conversations: [], total: 0 };
        }
        if (response.status === 403) {
          console.warn('Access denied to conversations - check permissions');
          return { conversations: [], total: 0 };
        }
        if (response.status === 401) {
          console.warn('Authentication failed - token may be expired');
          return { conversations: [], total: 0 };
        }
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { conversations: [], total: 0 };
    }
  }

  async getConversation(id: string, token?: string): Promise<any> {
    if (!token) {
      throw new Error('Authentication token required');
    }
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const response = await fetch(`${apiUrl}/api/conversations/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch conversation: ${response.status}`);
    }
    
    return response.json();
  }

  async deleteConversation(id: string, token?: string): Promise<void> {
    if (!token) {
      throw new Error('Authentication token required');
    }
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const response = await fetch(`${apiUrl}/api/conversations/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete conversation: ${response.status}`);
    }
  }

  async exportConversation(id: string, token?: string): Promise<any> {
    if (!token) {
      throw new Error('Authentication token required');
    }
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const response = await fetch(`${apiUrl}/api/conversations/${id}/export`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to export conversation: ${response.status}`);
    }
    
    return response.json();
  }
}

export default new ConversationService();